import { TIER_STAT_MULT, type Role, type UnitDef } from "@zend/content";

export interface StatBlock {
  hp: number;
  atk: number;
  def: number;
  /** Attack interval in 100 ms ticks. */
  intervalTicks: number;
  /** Critical hit chance 0..1. */
  crit: number;
  /** Critical damage multiplier. */
  critDmg: number;
  acc: number;
  eva: number;
  energyPerAttack: number;
  cargo: number;
}

export type StatKey = keyof StatBlock;

/** Base stats at level 1, tier D (30-units.md R20). Commander HP is a placeholder; combat uses the fighters' average. */
export const ROLE_BASE: Record<Role, StatBlock> = {
  defender: { hp: 1600, atk: 90, def: 160, intervalTicks: 12, crit: 0.05, critDmg: 1.5, acc: 100, eva: 0, energyPerAttack: 15, cargo: 800 },
  attacker: { hp: 1000, atk: 170, def: 90, intervalTicks: 10, crit: 0.1, critDmg: 1.5, acc: 100, eva: 0, energyPerAttack: 20, cargo: 600 },
  saboteur: { hp: 700, atk: 220, def: 60, intervalTicks: 8, crit: 0.25, critDmg: 1.75, acc: 110, eva: 30, energyPerAttack: 25, cargo: 400 },
  commander: { hp: 1200, atk: 110, def: 110, intervalTicks: 25, crit: 0.05, critDmg: 1.5, acc: 100, eva: 0, energyPerAttack: 12, cargo: 500 },
  support: { hp: 900, atk: 60, def: 100, intervalTicks: 15, crit: 0, critDmg: 1.5, acc: 100, eva: 0, energyPerAttack: 20, cargo: 700 },
};

export interface StatMods {
  level: number;
  stars: number;
  /** Chemistry bonus in percent applied to hp/atk/def. */
  chemistryPct: number;
  /** Percent bonuses per stat from modules (e.g. { atk: 12 }). */
  modulePct: Partial<Record<StatKey, number>>;
  /** Flat additions from modules applied last. */
  moduleFlat: Partial<Record<StatKey, number>>;
  /** Percent bonuses per stat from research, drone, protocols and skins. */
  researchPct: Partial<Record<StatKey, number>>;
  /** MegaVIP all-stat percent. */
  vipPct: number;
  /** Declared cosmetic bonuses per stat; a numeric value retains the legacy all-stat rules API. */
  skinPct: number | Partial<Record<StatKey, number>>;
}

export const NO_MODS: StatMods = { level: 1, stars: 0, chemistryPct: 0, modulePct: {}, moduleFlat: {}, researchPct: {}, vipPct: 0, skinPct: 0 };

const SCALED: StatKey[] = ["hp", "atk", "def"];

export function levelMultiplier(level: number): number {
  return 1 + 0.06 * (level - 1);
}

export function starMultiplier(stars: number): number {
  return 1 + 0.1 * stars;
}

/** Final stat pipeline. Intrinsic commander HP is only for formation-independent account progression; battle/roster keep their placeholder. */
export function unitStats(def: UnitDef, mods: StatMods, intrinsicCommanderHp = false): StatBlock {
  const base = ROLE_BASE[def.role];
  const tier = TIER_STAT_MULT[def.tier];
  const out: StatBlock = { ...base };
  for (const key of SCALED) {
    let v = base[key] * tier * levelMultiplier(mods.level) * starMultiplier(mods.stars);
    v *= 1 + mods.chemistryPct / 100;
    v *= 1 + (mods.researchPct[key] ?? 0) / 100;
    v *= 1 + (mods.modulePct[key] ?? 0) / 100;
    v *= 1 + mods.vipPct / 100;
    v *= 1 + (typeof mods.skinPct === "number" ? mods.skinPct : (mods.skinPct[key] ?? 0)) / 100;
    out[key] = Math.round(v + (mods.moduleFlat[key] ?? 0));
  }
  out.crit = Math.min(1, base.crit + (mods.modulePct.crit ?? 0) / 100 + (mods.researchPct.crit ?? 0) / 100);
  out.critDmg = base.critDmg + (mods.modulePct.critDmg ?? 0) / 100;
  out.acc = base.acc + (mods.moduleFlat.acc ?? 0) + (mods.researchPct.acc ?? 0);
  out.eva = base.eva + (mods.moduleFlat.eva ?? 0);
  out.energyPerAttack = Math.round(base.energyPerAttack * (1 + (mods.researchPct.energyPerAttack ?? 0) / 100) * (mods.stars >= 4 ? 1.1 : 1));
  out.cargo = Math.round(base.cargo * levelMultiplier(mods.level) * (mods.stars >= 4 ? 1.25 : 1) * (1 + (mods.researchPct.cargo ?? 0) / 100));
  const speedPct = (mods.modulePct.intervalTicks ?? 0) + (mods.researchPct.intervalTicks ?? 0);
  out.intervalTicks = Math.max(3, Math.round(base.intervalTicks / (1 + speedPct / 100)));
  if (def.role === "commander" && !intrinsicCommanderHp) out.hp = 0;
  return out;
}

/** Power score used for enclave power, matchmaking and sorting. */
export function unitPower(stats: StatBlock, abilityLevels: number[], role: Role = "attacker"): number {
  const hp = stats.hp || (role === "commander" ? ROLE_BASE.commander.hp : 0);
  const abilities = abilityLevels.reduce((s, l) => s + l * 15, 0);
  return Math.round(hp * 0.1 + stats.atk * 2 + stats.def * 2 + stats.crit * 200 + abilities);
}
